/**
 * One condition as the backend's `search` op wants it.
 *
 * The op takes the predicate grammar MINUS its computed forms (`transform`,
 * `{left, op, right}`, runtime `param`), which it rejects loudly rather than
 * ignoring — so this shape deliberately cannot express them.
 */
export interface SearchPropertyFilter extends Record<string, unknown> {
  field?: string;
  op?: string;
  value?: unknown;
  or?: SearchPropertyFilter[];
}

/**
 * A filter the analyst is building. Kept separate from the wire shape so a
 * half-finished chip can exist without being sent.
 */
export interface SearchFilterDraft {
  id: string;
  property: string;
  op: string;
  value: string;
}

export interface SearchOperatorOption {
  /** The wire value the search tool expects. */
  value: string;
  /** How it reads in the UI. */
  label: string;
  /** True when the operator takes no value (`exists`, `missing`, …). */
  nullary?: boolean;
  hint?: string;
}

/**
 * The operators the search tool accepts, in its own vocabulary.
 *
 * Mirrors `SUPPORTED_OPS` in the backend's search-filter compiler. Every string
 * matching operator here is case-insensitive, including regex.
 */
export const SEARCH_OPERATORS: SearchOperatorOption[] = [
  { value: '=', label: 'is' },
  { value: '!=', label: 'is not' },
  { value: 'contains', label: 'contains' },
  { value: 'notContains', label: 'does not contain' },
  { value: 'startsWith', label: 'starts with' },
  { value: 'notStartsWith', label: 'does not start with' },
  { value: 'endsWith', label: 'ends with' },
  { value: 'in', label: 'is one of', hint: 'Separate values with commas' },
  { value: '>', label: 'is greater than' },
  { value: '>=', label: 'is at least' },
  { value: '<', label: 'is less than' },
  { value: '<=', label: 'is at most' },
  {
    value: 'regex',
    label: 'matches pattern',
    // The engine is Lucene, not PCRE, so lookaround, back-references and the
    // \d/\w shorthands are all absent. Named in the reader's terms rather than
    // the engine's, because an opaque server error is the alternative.
    //
    // The anchoring is named FIRST because it is the only one of these that
    // fails silently: the backend builds a raw OpenSearch `regexp` query, which
    // matches the whole value, so `[0-9]` returns nothing while `.*[0-9].*`
    // returns 86 rows on the same field. Unstated, that reads as "no matches"
    // and sends the reader looking for a problem with their data.
    hint: 'Must match the whole value — wrap in .* to match part of it. Basic patterns only: no lookaround, back-references or \\d / \\w',
  },
  { value: 'exists', label: 'has any value', nullary: true },
  { value: 'missing', label: 'has no value', nullary: true },
  { value: 'non_empty', label: 'is not empty', nullary: true },
];

const NULLARY = new Set(
  SEARCH_OPERATORS.filter((operator) => operator.nullary).map(
    (operator) => operator.value,
  ),
);

export const isNullaryOperator = (op: string): boolean => NULLARY.has(op);

export function operatorLabel(op: string): string {
  // Falls back to a word, not the wire value: printing `notStartsWith` on a chip
  // leaks the backend's vocabulary into the reader's view.
  return (
    SEARCH_OPERATORS.find((entry) => entry.value === op)?.label ?? 'matches'
  );
}

/** The values an `in` filter is actually asking about. */
function listValues(raw: string): string[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** Whether a draft carries enough to be worth sending. */
export function isFilterComplete(draft: SearchFilterDraft): boolean {
  if (!draft.property.trim()) return false;
  if (isNullaryOperator(draft.op)) return true;
  // A list of only separators trims to nothing, and an empty `in` list is a
  // filter the backend accepts and that matches no record — so the search comes
  // back empty and the panel blames the query.
  if (draft.op === 'in') return listValues(draft.value).length > 0;
  return draft.value.trim() !== '';
}

/**
 * A draft as the search tool wants it.
 *
 * `in` takes a list, so its comma-separated text becomes an array; every other
 * operator sends the value as typed.
 */
export function toToolFilter(draft: SearchFilterDraft): SearchPropertyFilter {
  const field = draft.property.trim();
  if (isNullaryOperator(draft.op)) {
    return { field, op: draft.op };
  }
  if (draft.op === 'in') {
    return { field, op: 'in', value: listValues(draft.value) };
  }
  // Trimmed, like every other path on this surface: `isFilterComplete` and
  // `describeFilter` both trim, and `in` trims each entry — so a value typed as
  // "  open  " showed on the chip as `status is open` while the wire carried the
  // padding, and an exact or prefix match came back empty with nothing on screen
  // explaining why.
  return { field, op: draft.op, value: draft.value.trim() };
}

/** Every complete draft, as the wire shape. Incomplete chips are left out. */
export function toToolFilters(
  drafts: SearchFilterDraft[],
): SearchPropertyFilter[] {
  return drafts.filter(isFilterComplete).map(toToolFilter);
}

/** A filter in words, for the chip face. */
export function describeFilter(draft: SearchFilterDraft): string {
  const label = operatorLabel(draft.op);
  const property = draft.property.trim() || 'property';
  return isNullaryOperator(draft.op)
    ? `${property} ${label}`
    : `${property} ${label} ${draft.value.trim()}`;
}

/**
 * Which chip an error belongs to, when the server refused a filter.
 *
 * The backend raises rather than dropping a filter it cannot apply, and names
 * the offending property in the message. Attributing it to the chip is what
 * turns an opaque whole-search failure into a fixable one.
 */
/**
 * Whether a message is a filter refusal at all.
 *
 * Attribution used to run on ANY failure, so a chip on a property called
 * `status` was painted red by "Request failed with status code 500" — telling
 * the reader to fix a filter that was fine while the real fault went unnamed.
 */
function isFilterRefusal(message: string): boolean {
  return /\bfilter\b|unknown property|not mapped|unmapped|unsupported op/i.test(
    message,
  );
}

export function attributeFilterError(
  message: string,
  drafts: SearchFilterDraft[],
): string | null {
  if (!isFilterRefusal(message)) return null;
  const named = drafts
    .filter((draft) => draft.property.trim() !== '')
    // Longest first: a bare substring test pinned "Unknown property: full_name"
    // on a `name` chip, so the reader edited a perfectly good filter while the
    // broken one stayed unmarked.
    .sort(
      (left, right) =>
        right.property.trim().length - left.property.trim().length,
    )
    .find((draft) => mentionsProperty(message, draft.property.trim()));
  return named?.id ?? null;
}

/**
 * Whether an error names this property, rather than merely containing its
 * letters. Bounded on both sides so `name` does not match inside `full_name`.
 */
function mentionsProperty(message: string, property: string): boolean {
  const escaped = property.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^A-Za-z0-9_])${escaped}([^A-Za-z0-9_]|$)`).test(
    message,
  );
}

/**
 * A wire-shape filter in words.
 *
 * The chip face has `describeFilter` for a draft the reader is editing; this is
 * its counterpart for a filter already stored on a recipe, where only
 * `{field, op, value}` survives. Both go through `operatorLabel`, so a sheet
 * describes its conditions in the same words the chip that built them used.
 */
export function describeToolFilter(filter: Record<string, unknown>): string {
  const alternatives = filter.or;
  if (Array.isArray(alternatives) && alternatives.length > 0) {
    return alternatives
      .map((entry) => describeToolFilter(entry as Record<string, unknown>))
      .join(' or ');
  }
  const field = String(filter.field ?? '').trim() || 'property';
  const op = String(filter.op ?? '=');
  const label = operatorLabel(op);
  if (isNullaryOperator(op)) return `${field} ${label}`;
  // `in` travels as an array, and printing it raw gives "Member,Guest" with no
  // spacing — the one shape that does not read as the sentence it is.
  const value = Array.isArray(filter.value)
    ? filter.value.map((entry) => String(entry)).join(', ')
    : String(filter.value ?? '');
  return `${field} ${label} ${value}`;
}

/**
 * A server refusal as an analyst should read it.
 *
 * The backend's filter errors are written for an MCP agent and close with a
 * standing suggestion to "run it as a run_pipelines select_label(predicates=…)
 * step instead". That is sound advice for a tool caller and useless to someone
 * looking at a search box: it names an interface they have no access to, and it
 * is the last thing they read, so it displaces the part that actually tells
 * them what to fix. The sentence before it — which property, which type, which
 * operators would work — is kept verbatim.
 */
const AGENT_HINT = /\s*For a filter search cannot express, run it as a[\s\S]*$/;

export function readableSearchError(message: string): string {
  return message.replace(AGENT_HINT, '').trim();
}
